/**
 * Saving and showing a Frappe file on the deployed app.
 *
 * `server/index.ts` gates every `/api/*` request on the Clerk bearer and answers 401 without it —
 * and a plain `<a href download>`, `<img src>` or `window.open` sends no headers at all. (The pdf
 * preview's "The server returned an error (401)", 2026-09-21; the same 401 sat behind every
 * download link and image attachment on Waves.) So a file is fetched the way the previews fetch
 * it — `attachmentBlob.ts`'s loader, bearer attached, one download shared with the preview —
 * and handed to the browser as an object URL.
 *
 * In dev the Vite proxy injects the token, so the old links worked there; that is why this
 * went unnoticed until someone opened an attachment on the deployed app.
 */
import { useEffect, useState } from 'react'
import { useCachedAttachmentLoader } from './attachmentBlob'

/** The browser calls, injectable so the flow is testable under node. */
export interface SaveSurface {
  createObjectURL: (blob: Blob) => string
  revokeObjectURL: (url: string) => void
  /** Click a transient `<a href download>` — the one way a page can start a save. */
  click: (href: string, name: string) => void
}

const browserSurface: SaveSurface = {
  createObjectURL: (blob) => URL.createObjectURL(blob),
  // The click consumes the URL synchronously; the grace keeps Safari's save from racing it.
  revokeObjectURL: (url) => setTimeout(() => URL.revokeObjectURL(url), 1000),
  click: (href, name) => {
    const a = document.createElement('a')
    a.href = href
    a.download = name
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
  },
}

/** Fetch `url` through `loadBlob` and save it as `name`. Throws what the loader throws. */
export async function downloadFile(url: string, name: string, loadBlob: (url: string) => Promise<Blob>, dom: SaveSurface = browserSurface): Promise<void> {
  const blob = await loadBlob(url)
  const href = dom.createObjectURL(blob)
  try {
    dom.click(href, name)
  } finally {
    dom.revokeObjectURL(href)
  }
}

/** A bearer-fetched object URL for `url` (for `<img src>`), `null` until loaded or when it fails. */
export function useFileObjectUrl(url: string | null): { src: string | null; error: string | null } {
  const loadBlob = useCachedAttachmentLoader()
  const [state, setState] = useState<{ src: string | null; error: string | null }>({ src: null, error: null })
  useEffect(() => {
    if (!url) {
      setState({ src: null, error: null })
      return
    }
    let objectUrl: string | null = null
    let cancelled = false
    loadBlob(url)
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setState({ src: objectUrl, error: null })
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ src: null, error: error instanceof Error ? error.message : String(error) })
      })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [url, loadBlob])
  return state
}
